import React, { useState, useEffect, useRef } from "react";
import "./BookingForm.css";
import { storage } from "../utils/storage";

/**
 * BookingForm Component
 * ---------------------
 * A controlled React form component for managing restaurant table reservations.
 * It includes real-time validation, accessibility features, and localStorage persistence.
 *
 * UX Features:
 * - Autofocus and automatic field progression for faster input
 * - Client-side validation with instant feedback
 * - Local storage support for persisting form data
 * - Reset functionality for clearing all fields and errors
 * - Status messages for submission feedback
 *
 * @component
 * @param {Object} props - Component properties.
 * @param {Array<string>} props.availableTimes - List of available time slots for the selected date.
 * @param {Function} props.dispatch - Reducer dispatch function for updating available times.
 * @param {Function} props.onDateChange - Callback triggered when a new date is selected.
 * @param {Function} props.submitForm - Function to handle form submission.
 *
 * @returns {JSX.Element} Rendered booking form element.
 *
 * @example
 * // Usage:
 * <BookingForm
 *   availableTimes={availableTimes}
 *   dispatch={dispatch}
 *   onDateChange={handleDateChange}
 *   submitForm={submitForm}
 * />
 */

function BookingForm({ availableTimes, dispatch, onDateChange, submitForm }) {
  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const dateRef = useRef(null);
  const timeRef = useRef(null);
  const guestsRef = useRef(null);
  const occasionRef = useRef(null);

  const [fullName, setFullName] = useState(storage.get("fullName") || "");
  const [email, setEmail] = useState(storage.get("email") || "");
  const [date, setDate] = useState(storage.get("date") || "");
  const [time, setTime] = useState(storage.get("time") || "");
  const [guests, setGuests] = useState(storage.get("guests") || 1);
  const [occasion, setOccasion] = useState(storage.get("occasion") || "");
  const [isValid, setIsValid] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");

  const today = new Date().toISOString().split("T")[0];

  useEffect(() => {
    if (nameRef.current) nameRef.current.focus();
  }, []);

  const validateForm = () => {
    const newErrors = {};
    if (!fullName) newErrors.fullName = "Please enter your full name.";
    if (!email) newErrors.email = "Please enter your email address.";
    else if (!/\S+@\S+\.\S+/.test(email))
      newErrors.email = "Please enter a valid email address.";
    if (!date) newErrors.date = "Please select a date.";
    else if (date < today) newErrors.date = "Date cannot be in the past.";
    if (!time) newErrors.time = "Please select a time.";
    if (!guests || guests < 1 || guests > 10)
      newErrors.guests = "Guests must be between 1 and 10.";
    if (!occasion) newErrors.occasion = "Please select an occasion.";
    setErrors(newErrors);
    setIsValid(Object.keys(newErrors).length === 0);
  };

  useEffect(() => {
    validateForm();
  }, [fullName, email, date, time, guests, occasion]);

  const handleChange = (setter, key, nextRef) => (e) => {
    setter(e.target.value);
    storage.set(key, e.target.value);
    if (nextRef && e.target.value && nextRef.current) nextRef.current.focus();
  };

  const handleDateChange = (e) => {
    const selectedDate = e.target.value;
    setDate(selectedDate);
    onDateChange(selectedDate);
    dispatch({ type: "UPDATE_TIMES", date: selectedDate });
    storage.set("date", selectedDate);
    if (timeRef.current) timeRef.current.focus();
  };

  const handleGuestsChange = (e) => {
    let value = Number(e.target.value);
    if (value < 1 || value > 10) {
      setErrors((prev) => ({
        ...prev,
        guests: "Guests must be between 1 and 10.",
      }));
    } else {
      setErrors((prev) => {
        const { guests, ...rest } = prev;
        return rest;
      });
    }
    setGuests(value);
    storage.set("guests", value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    validateForm();
    if (Object.keys(errors).length === 0) {
      setIsSubmitting(true);
      setStatusMessage("Submitting your reservation...");
      const formData = {
        fullName,
        email,
        date,
        time,
        guests: guests.toString(),
        occasion,
      };
      await new Promise((resolve) => setTimeout(resolve, 1500));
      submitForm(formData);
      setIsSubmitting(false);
      setStatusMessage("Reservation successfully submitted!");
      if (nameRef.current) nameRef.current.focus();
    }
  };

  const handleReset = () => {
    setFullName("");
    setEmail("");
    setDate("");
    setTime("");
    setGuests(1);
    setOccasion("");
    setErrors({});
    setIsValid(false);
    setStatusMessage("");
    storage.remove("fullName");
    storage.remove("email");
    storage.remove("date");
    storage.remove("time");
    storage.remove("guests");
    storage.remove("occasion");
    if (nameRef.current) nameRef.current.focus();
  };

  return (
    <form
      className="booking-form"
      onSubmit={handleSubmit}
      noValidate
      aria-label="Reservation form"
    >
      <legend>Reservation Details</legend>
      <fieldset>
        <label htmlFor="full-name">Full Name</label>
        <input
          ref={nameRef}
          type="text"
          id="full-name"
          value={fullName}
          onChange={handleChange(setFullName, "fullName", emailRef)}
          required
          placeholder="e.g. John Doe"
          aria-describedby={errors.fullName ? "name-error" : undefined}
          autoComplete="name"
        />
        {errors.fullName && (
          <span id="name-error" className="error-message">
            {errors.fullName}
          </span>
        )}

        <label htmlFor="email">Email Address</label>
        <input
          ref={emailRef}
          type="email"
          id="email"
          value={email}
          onChange={handleChange(setEmail, "email", dateRef)}
          required
          placeholder="e.g. john@example.com"
          aria-describedby={errors.email ? "email-error" : undefined}
          autoComplete="email"
        />
        {errors.email && (
          <span id="email-error" className="error-message">
            {errors.email}
          </span>
        )}

        <label htmlFor="res-date">Select your date</label>
        <input
          ref={dateRef}
          type="date"
          id="res-date"
          value={date}
          onChange={handleDateChange}
          required
          min={today}
          aria-describedby={errors.date ? "date-error" : undefined}
        />
        {errors.date && (
          <span id="date-error" className="error-message">
            {errors.date}
          </span>
        )}

        <label htmlFor="res-time">Select your time</label>
        <select
          ref={timeRef}
          id="res-time"
          value={time}
          onChange={handleChange(setTime, "time", guestsRef)}
          required
          aria-describedby={errors.time ? "time-error" : undefined}
        >
          <option value="">Choose available time</option>
          {availableTimes && availableTimes.length > 0 ? (
            availableTimes.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))
          ) : (
            <option value="">No available times</option>
          )}
        </select>
        {errors.time && (
          <span id="time-error" className="error-message">
            {errors.time}
          </span>
        )}

        <label htmlFor="guests">How many guests?</label>
        <input
          ref={guestsRef}
          type="number"
          id="guests"
          min="1"
          max="10"
          value={guests}
          onChange={handleGuestsChange}
          required
          placeholder="1–10 guests"
          aria-describedby={errors.guests ? "guests-error" : undefined}
        />
        {errors.guests && (
          <span id="guests-error" className="error-message">
            {errors.guests}
          </span>
        )}

        <label htmlFor="occasion">What’s the occasion?</label>
        <select
          ref={occasionRef}
          id="occasion"
          value={occasion}
          onChange={handleChange(setOccasion, "occasion")}
          required
          aria-describedby={errors.occasion ? "occasion-error" : undefined}
        >
          <option value="">Select an occasion</option>
          <option value="Birthday">Birthday</option>
          <option value="Anniversary">Anniversary</option>
          <option value="Wedding">Wedding</option>
          <option value="Other">Other</option>
        </select>
        {errors.occasion && (
          <span id="occasion-error" className="error-message">
            {errors.occasion}
          </span>
        )}

        <div className="button-group">
          <button
            type="submit"
            aria-label="Submit reservation form"
            disabled={!isValid || isSubmitting}
            className={!isValid || isSubmitting ? "disabled-btn" : ""}
          >
            {isSubmitting ? "Submitting..." : "Submit Reservation"}
          </button>

          <button
            type="button"
            className="reset-btn"
            onClick={handleReset}
            disabled={isSubmitting}
          >
            Reset Form
          </button>
        </div>

        {statusMessage && (
          <p
            className={`status-message ${isSubmitting ? "loading" : "success"}`}
          >
            {statusMessage}
          </p>
        )}
      </fieldset>
    </form>
  );
}

export default BookingForm;
